<template>
  <EChartCard
    :title="$t('inspectionsDue')"
    :buildOption="buildOption"
    :height="360"
    @chart-click="handleChartClick"
  />
</template>

<script setup>
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { getSequential, statusColors } from '@/utils/vizPalette';
import { axisColor, DIM_OPACITY } from '@/utils/chartTheme';
import { nextMonths } from '@/utils/dateAxis';
import { useSelectableFilter } from '@/composables/useSelectableFilter';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();

const chartName = 'inspectionsDueChart';
const OVERDUE = '__overdue__';

const { isSelected, hasSelection, sync, toggle } = useSelectableFilter(chartName);

// Unique assets, bucketed by the month their next inspection falls in.
// `isOverdue` already excludes assets inspected since the date was set.
const query = `
  SELECT '${OVERDUE}' AS bucket, COUNT(*) AS value
  FROM filteredAssets
  WHERE isOverdue
  UNION ALL
  SELECT strftime(nextInspectionDate, '%Y-%m') AS bucket, COUNT(*) AS value
  FROM filteredAssets
  WHERE nextInspectionDate >= CURRENT_DATE AND NOT inspectedSinceDue
  GROUP BY 1
`;

const buildOption = async () => {
  sync();
  const conn = store.getters.getDBConnection;
  if (!conn) return null;

  const result = await conn.query(query);
  const counts = {};
  result.toArray().forEach((row) => {
    const json = row.toJSON();
    counts[json.bucket] = Number(json.value) || 0;
  });

  const fontColor = store.getters.chartFontColor;
  const barColor = getSequential(store.state.theme)[2];
  const gridColor = axisColor(store.state.theme);

  const overdue = counts[OVERDUE] || 0;
  const buckets = [{ key: OVERDUE, label: t('overdue'), value: overdue, color: statusColors.serious }];
  // The next 12 calendar months, zero-filled, starting with the current one.
  nextMonths(12).forEach((month) => {
    buckets.push({ key: month.key, label: month.label, value: counts[month.key] || 0, color: barColor });
  });

  if (buckets.every((bucket) => bucket.value === 0)) return null;

  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 8, right: 16, top: 32, bottom: 4, containLabel: true },
    xAxis: {
      type: 'category',
      data: buckets.map((bucket) => bucket.label),
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: fontColor, hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: fontColor },
      splitLine: { lineStyle: { color: gridColor } },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 22,
        data: buckets.map((bucket) => ({
          value: bucket.value,
          bucketKey: bucket.key,
          itemStyle: {
            color: bucket.color,
            borderRadius: [4, 4, 0, 0],
            opacity: hasSelection.value && !isSelected(bucket.key) ? DIM_OPACITY : 1,
          },
          label: bucket.key === OVERDUE
            ? { show: overdue > 0, position: 'top', color: fontColor, formatter: () => `⚠ ${overdue}` }
            : undefined,
        })),
      },
    ],
  };
};

const handleChartClick = async (params) => {
  const key = params?.data?.bucketKey;
  if (!key) return;

  const condition = key === OVERDUE
    ? 'isOverdue'
    : `strftime(nextInspectionDate, '%Y-%m') = ${sqlLiteral(key)} AND NOT inspectedSinceDue`;
  const label = key === OVERDUE ? t('overdue') : `${t('nextInspection')}: ${key}`;

  await toggle(key, condition, label);
};
</script>
